import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../App'
import { api, User } from '../services/api'

export default function ProfilePage() {
  const { userId } = useParams<{ userId?: string }>()
  const { user: currentUser, logout } = useAuth()
  const navigate = useNavigate()

  const [profileUser, setProfileUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const [publicKeyFingerprint, setPublicKeyFingerprint] = useState<string>('')

  const isOwnProfile = !userId || userId === currentUser?.username

  useEffect(() => {
    if (isOwnProfile) {
      // Show current user's profile
      if (currentUser) {
        setProfileUser({
          id: currentUser.id,
          username: currentUser.username,
        })
        // Get public key fingerprint from localStorage
        const identityKey = localStorage.getItem('lynkstar_identity_key')
        if (identityKey) {
          try {
            const parsed = JSON.parse(identityKey)
            // Show first 8 chars of public key as fingerprint
            setPublicKeyFingerprint(parsed.publicKey.substring(0, 16))
          } catch {
            setPublicKeyFingerprint('Not available')
          }
        }
      }
    } else {
      // Load other user's profile
      loadUserProfile(userId)
    }
  }, [userId, currentUser, isOwnProfile])

  const loadUserProfile = async (username: string) => {
    setIsLoading(true)
    try {
      const user = await api.getUserByUsername(username)
      setProfileUser(user)
      if (user.publicKey) {
        setPublicKeyFingerprint(user.publicKey.substring(0, 16))
      }
    } catch (error) {
      console.error('Failed to load profile:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleStartChat = () => {
    if (profileUser) {
      navigate(`/chat/${profileUser.username}`)
    }
  }

  const handleBlockUser = async () => {
    if (profileUser) {
      try {
        await api.blockUser(profileUser.id)
        navigate(-1)
      } catch (error) {
        console.error('Failed to block user:', error)
      }
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-dark-bg">
        <div className="spinner border-primary border-t-primary-light" />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-dark-bg">
      {/* Header */}
      <header className="app-bar">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-white/10 rounded-full transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-lg font-semibold flex-1">
          {isOwnProfile ? 'My Profile' : 'Profile'}
        </h1>
        {isOwnProfile && (
          <button
            onClick={() => navigate('/settings')}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        )}
      </header>

      {/* Profile Content */}
      <main className="flex-1 overflow-y-auto">
        {/* Avatar Section */}
        <div className="flex flex-col items-center py-8 px-4">
          <div className="w-24 h-24 bg-primary rounded-full flex items-center justify-center text-white text-3xl font-semibold shadow-lg">
            {profileUser?.avatar ? (
              <img
                src={profileUser.avatar}
                alt={profileUser.username}
                className="w-full h-full object-cover rounded-full"
              />
            ) : (
              profileUser?.username?.charAt(0).toUpperCase()
            )}
          </div>
          <h2 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">
            {profileUser?.username}
          </h2>
          {profileUser?.isOnline !== undefined && (
            <p className={`text-sm mt-1 ${profileUser.isOnline ? 'text-secure-green' : 'text-gray-500'}`}>
              {profileUser.isOnline ? 'Online' : 'Offline'}
            </p>
          )}
        </div>

        {/* Security Section */}
        <div className="px-4 mb-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span className="font-semibold text-blue-700 dark:text-blue-300">
                Security Key Fingerprint
              </span>
            </div>
            <p className="text-sm text-blue-600 dark:text-blue-400 font-mono">
              {publicKeyFingerprint || 'Loading...'}
            </p>
            <p className="text-xs text-blue-500 dark:text-blue-500 mt-2">
              Compare this with your contact to verify their identity
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="px-4 space-y-3">
          {!isOwnProfile && (
            <>
              <button
                onClick={handleStartChat}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                Send Message
              </button>
              <button
                onClick={() => navigate(`/call/${profileUser?.username}?type=audio`)}
                className="w-full px-4 py-3 bg-gray-100 dark:bg-dark-surface hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl flex items-center justify-center gap-2 text-gray-700 dark:text-white transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                Voice Call
              </button>
              <button
                onClick={() => navigate(`/call/${profileUser?.username}?type=video`)}
                className="w-full px-4 py-3 bg-gray-100 dark:bg-dark-surface hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl flex items-center justify-center gap-2 text-gray-700 dark:text-white transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Video Call
              </button>
            </>
          )}

          {isOwnProfile ? (
            <button
              onClick={() => setShowLogoutConfirm(true)}
              className="w-full px-4 py-3 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-xl flex items-center justify-center gap-2 text-red-600 dark:text-red-400 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          ) : (
            <button
              onClick={handleBlockUser}
              className="w-full px-4 py-3 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-xl flex items-center justify-center gap-2 text-red-600 dark:text-red-400 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
              Block User
            </button>
          )}
        </div>

        {/* Info Section */}
        <div className="px-4 mt-8">
          <div className="text-center text-sm text-gray-500 dark:text-gray-400">
            <div className="flex items-center justify-center gap-1 mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-secure-green" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span>Protected by Signal Protocol</span>
            </div>
            <p>All messages are end-to-end encrypted</p>
          </div>
        </div>
      </main>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowLogoutConfirm(false)}>
          <div
            className="bg-white dark:bg-dark-surface w-full max-w-sm rounded-2xl p-6 animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Logout?
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              Your encryption keys are stored locally. Make sure you have a backup before logging out.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl text-gray-700 dark:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 px-4 py-3 bg-red-500 hover:bg-red-600 rounded-xl text-white transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
